import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';

import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';

import useCurrentUser from '~/hooks/useCurrentUser';

import UsersService from '~/services/UsersService';

import { UserType } from '~/types/responses';

interface UserWithOrders {
  user: UserType;
  orders: Array<Record<string, unknown>>;
}

interface Props {
  usersWithOrders: Array<UserWithOrders>;
}

export default function CallCentreOpeningLine({ usersWithOrders }: Props) {
  const { user: currentUser } = useCurrentUser();
  const { enqueueSnackbar } = useSnackbar();
  const tenant = useSelector((state: App.State) => state.tenant);

  const [isSendingSms, setIsSendingSms] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [countdown, setCountdown] = useState(30);

  const isValidCode = /^\d{6}$/.test(code);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((curr) => Math.max(curr - 1, 0));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [countdown]);

  if (usersWithOrders.length === 0) {
    return null;
  }

  const user = usersWithOrders[0]?.user;
  const phonePrefix = user?.phone_prefix;
  const phone = user?.phone;
  const hasPhoneNumber = phonePrefix && phone;

  const isSingleUserWithSingleOrder = usersWithOrders.length === 1 && usersWithOrders[0].orders.length === 1;
  const firstName = currentUser?.givenName || 'there';

  const handleSendSms = async () => {
    if (!hasPhoneNumber) {
      enqueueSnackbar('No phone number available', { variant: 'error' });
      return;
    }

    setIsSendingSms(true);
    setErrorMessage(null);

    try {
      await UsersService.sendOtpSmsCode(phonePrefix, phone, tenant.brand);
      enqueueSnackbar('SMS verification code sent successfully', { variant: 'success' });
      setSmsSent(true);
      setCountdown(30);
    } catch (error) {
      enqueueSnackbar(`Failed to send SMS: ${error.message || 'Unknown error'}`, { variant: 'error' });
    } finally {
      setIsSendingSms(false);
    }
  };

  const handleVerify = async () => {
    setErrorMessage(null);

    if (!isValidCode) {
      setErrorMessage('Please enter 6 digits only');
      return;
    }

    if (!hasPhoneNumber) {
      setErrorMessage('No phone number available');
      return;
    }

    setIsVerifying(true);

    try {
      await UsersService.validateOtpSmsCode(phonePrefix, phone, code, tenant.brand);
      setIsVerified(true);
      enqueueSnackbar('Customer verified successfully!', { variant: 'success' });
    } catch (error) {
      setErrorMessage(error.message ?? 'Unknown Error');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    setErrorMessage(null);
    setCountdown(30);

    if (!hasPhoneNumber) {
      setErrorMessage('No phone number available');
      return;
    }

    setIsSendingSms(true);

    try {
      await UsersService.sendOtpSmsCode(phonePrefix, phone, tenant.brand);
      enqueueSnackbar('Code sent to customer', { variant: 'success' });
    } catch (error) {
      setErrorMessage(error.message ?? 'Unknown Error');
    } finally {
      setIsSendingSms(false);
    }
  };

  return (
    <Box mb={3}>
      <Typography variant="body1" color="text.secondary">
        Suggested opening line:
        <br />
        Welcome to Luxury Escapes, you are speaking with {firstName}. Can I start with your first name please?
        <br />
        (customer responds)
        <br />
        Thanks, [name]. I've got your account in front of me.
        <br />
        Before we go further, I just need to verify you, can you please read back the 6 digit code we've sent to your
        mobile?
        <br />
        {!smsSent && !isVerified && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleSendSms}
            disabled={isSendingSms || !hasPhoneNumber}
            sx={{ mt: 1 }}
          >
            {isSendingSms ? 'Sending...' : 'Send SMS Verification Code'}
          </Button>
        )}
        {smsSent && !isVerified && (
          <Box sx={{ mt: 2 }}>
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
            )}

            <Typography variant="body2" sx={{ mb: 1 }}>
              The customer has received a text on their phone. Please ask them to read it to you out loud.
            </Typography>

            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <TextField
                label="Verification Code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                size="small"
                sx={{ width: 150 }}
                placeholder="6 digits"
              />
              <Button onClick={handleVerify} variant="contained" disabled={!code || isVerifying} loading={isVerifying}>
                Verify
              </Button>
            </Stack>

            <Button onClick={handleResendCode} variant="outlined" size="small" disabled={countdown > 0 || isSendingSms}>
              {countdown > 0 ? `Resend Code (${countdown}s)` : 'Resend Code'}
            </Button>
          </Box>
        )}
        {isVerified && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Customer verified! You may action anything related to accounts or orders on this page, without further
            verification.
            <br />
            (No need to confirm name, booking number, email, hotel, etc)
          </Alert>
        )}
        <br />
        <Typography variant="body2" fontStyle="italic" sx={{ mt: 1 }}>
          Once SMS-verified, you may action anything related to an account or order on this page,{' '}
          <Typography component="span" fontWeight="bold">
            without any further verification
          </Typography>{' '}
          (email, booking number, etc)
        </Typography>
        {isSingleUserWithSingleOrder ? 'Found one single user with one single order' : 'Found multiple.'}
      </Typography>
    </Box>
  );
}
